import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
} from '@angular/core';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import type { FullCandidate } from '@core/candidate-types';
import { ShareCandidatesWithContextAnalytics } from '@core/candidate-util';
import { trackByProp } from '@core/common-legacy';
import type { Company } from '@core/company-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { CandidateShareContainer } from '@core/sharing-container-types';
import {
  BoxComponent,
  ButtonComponent,
  ClipboardCopy,
  DividerComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  NewItemButtonComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { TruncatePipe } from './truncate.pipe';

@Component({
  selector: 'rt-candidate-sharing-anyone-tab',
  imports: [
    CommonModule,
    HeadingComponent,
    NewItemButtonComponent,
    TranslatePipe,
    IconComponent,
    BoxComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    FormFieldComponent,
    IconButtonComponent,
    InputComponent,
    TruncatePipe,
  ],
  templateUrl: './candidate-sharing-anyone-tab.component.html',
  styleUrls: ['./candidate-sharing-anyone-tab.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ShareCandidatesWithContextAnalytics,
    ...provideAnalyticsViewContext(AnalyticsViewContext.CopyPublicLinkDropdown),
  ],
})
export class CandidateSharingAnyoneTabComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly windowRef = inject(WindowRef);
  private readonly shareCandidatesAnalytics = inject(ShareCandidatesWithContextAnalytics);
  private readonly clipboardCopy = inject(ClipboardCopy);

  public readonly candidate = input<FullCandidate>(undefined!);
  public readonly currentCompany = input<Company>(undefined!);
  public readonly containers = input<CandidateShareContainer[]>(undefined!);

  public newLinkClick = output();
  public editContainerClick = output<CandidateShareContainer>();
  public addToExisting = output();

  public shareAgainExpanded = false;
  public inActiveShare = false;
  public trackById = trackByProp({ prop: 'id' });

  public ngOnChanges() {
    this.inActiveShare = !!this.containers().find(container => container.active);
  }

  public toggleShareAgain() {
    this.shareAgainExpanded = !this.shareAgainExpanded;
    this.cdRef.detectChanges();
  }

  public handleShareUrlIconClick(container: CandidateShareContainer): void {
    const shareUrl = container.shareUrl || '';
    if (shareUrl) this.windowRef.open(shareUrl, '_blank');
  }

  public copy(element: InputComponent): void {
    if (element && element.input.nativeElement) {
      this.shareCandidatesAnalytics.trackCopyPublicLink();
      this.clipboardCopy.fromHtmlElement(element.input.nativeElement);
    }
  }
}
